import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  UserPlus,
  Mail,
  Shield,
  Copy,
  ExternalLink
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';

export function TeamSettings() {
  const inviteLink = `${window.location.origin}/invite/abc123-def456-ghi789`;

  const copyInviteLink = () => {
    navigator.clipboard.writeText(inviteLink);
    toast({
      title: 'Link copied',
      description: 'Invite link has been copied to clipboard.',
    });
  };

  const teamSettings = {
    allowSelfRegistration: true,
    requireEmailVerification: true,
    defaultRole: 'PLAYER',
    autoApproveInvites: false,
    sessionRecording: true,
    dataSharing: false
  };

  const pendingInvites = [
    {
      email: 'alex.smith@email.com',
      role: 'PLAYER',
      sentDate: '2024-01-15',
      status: 'pending'
    },
    {
      email: 'sarah.johnson@email.com',
      role: 'COACH',
      sentDate: '2024-01-14',
      status: 'pending'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Team Invitations */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <UserPlus className="mr-2 h-5 w-5" />
            Team Invitations
          </CardTitle>
          <CardDescription>
            Invite new members to join your organization
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex space-x-2">
            <Input
              placeholder="Enter email address"
              className="flex-1"
            />
            <Button>
              <Mail className="mr-2 h-4 w-4" />
              Send Invite
            </Button>
          </div>

          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium mb-2">Organization Invite Link</h4>
            <div className="flex items-center space-x-2">
              <Input
                value={inviteLink}
                readOnly
                className="font-mono text-sm"
              />
              <Button variant="outline" size="sm" onClick={copyInviteLink}>
                <Copy className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm">
                <ExternalLink className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Anyone with this link can join your organization
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Pending Invites */}
      <Card>
        <CardHeader>
          <CardTitle>Pending Invitations</CardTitle>
          <CardDescription>
            Manage outstanding team invitations
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {pendingInvites.map((invite, index) => (
              <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center space-x-3">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback>
                      {invite.email[0].toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{invite.email}</p>
                    <div className="flex items-center space-x-2">
                      <Badge variant="outline">{invite.role}</Badge>
                      <span className="text-xs text-muted-foreground">
                        Sent {new Date(invite.sentDate).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <Button variant="ghost" size="sm">
                    Resend
                  </Button>
                  <Button variant="ghost" size="sm">
                    Cancel
                  </Button>
                </div>
              </div>
            ))}
            {pendingInvites.length === 0 && (
              <p className="text-center text-muted-foreground py-8">
                No pending invitations
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Team Permissions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Shield className="mr-2 h-5 w-5" />
            Team Permissions
          </CardTitle>
          <CardDescription>
            Configure default permissions for team members
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Allow Self Registration</Label>
                <p className="text-sm text-muted-foreground">
                  Let users join your organization without an invitation
                </p>
              </div>
              <Switch defaultChecked={teamSettings.allowSelfRegistration} />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Require Email Verification</Label>
                <p className="text-sm text-muted-foreground">
                  New members must verify their email before accessing
                </p>
              </div>
              <Switch defaultChecked={teamSettings.requireEmailVerification} />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Auto-approve Invites</Label>
                <p className="text-sm text-muted-foreground">
                  Automatically approve membership requests
                </p>
              </div>
              <Switch defaultChecked={teamSettings.autoApproveInvites} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data & Privacy */}
      <Card>
        <CardHeader>
          <CardTitle>Data & Privacy</CardTitle>
          <CardDescription>
            Control how team data is handled and shared
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Session Recording</Label>
              <p className="text-sm text-muted-foreground">
                Allow recording of training sessions
              </p>
            </div>
            <Switch defaultChecked={teamSettings.sessionRecording} />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Anonymous Data Sharing</Label>
              <p className="text-sm text-muted-foreground">
                Share anonymized performance data for research
              </p>
            </div>
            <Switch defaultChecked={teamSettings.dataSharing} />
          </div>
        </CardContent>
      </Card>

      {/* Save Changes */}
      <div className="flex justify-end">
        <Button>Save Team Settings</Button>
      </div>
    </div>
  );
}